import { i18n } from "@/plugins/i18n";
import { customFieldsActions } from "@nextories/vue-core-components/src/store/common";
import { projectsActions } from "@nextories/vue-tasks/src/store/common";
import fieldUtils from "@nextories/vue-tasks/src/utils/helpers/common/fieldUtils";
import formatHelpers from "@nextories/vue-tasks/src/utils/helpers/common/format";
import projectsHelper from "@nextories/vue-tasks/src/utils/helpers/projects";
import type { Project, Address, DynamicAddress, AddressKey, NextSheet } from "@/types/Projects";
import type { FormConfig, Field, Step } from "@nextories/vue-core-components/src/types/FormConfig";
import {useMeetingStore} from "@nextories/vue-core-components/src/store/meetingStore";
import {useDashboardStore} from "@/store/dashboardStore";

function formatAddress(prefix: AddressKey, dgt?: DynamicAddress, address?: Address) {
  return ['street', 'postal', 'city', 'country'].reduce((acc, key) => {
    const getKey = key === 'postal' ? 'postalcode' : key;
    const prefixWithKey = `${prefix}_${getKey}`;
    if (dgt) {
      // Formatting: pulling data from `dgt` with prefixed keys
      acc[key] = dgt[prefixWithKey];
    } else if (address) {
      // Exploding: setting prefixed keys from `address`
      acc[prefixWithKey] = address[key];
    }
    return acc;
  }, {} as DynamicAddress);
}

function setInitialValue(field: Field, value: any | [], id: string, parentField?: Field) {
  const data = {[id]: value};
  // Check if the value has changed compared to the initial value
  const isValueChanged = formatHelpers.checkObject(field.value, false)
    ? !formatHelpers.checkDeepEqual(field.initialValue, value)
    : field.initialValue !== value;
  // Check if there is at least one nested key in the data related to field.name or field.id
  const hasLeastOneNestedKey = id &&
    (formatHelpers.hasLeastOneNestedKey(data, field.name)
      || formatHelpers.hasLeastOneNestedKey(data, field.id));
  if ((field.name === id || field.id === id || hasLeastOneNestedKey)
    && (field.value === undefined
      || isValueChanged
      || Array.isArray(field.value))) {
    // Format the value based on whether there's at least one nested key
    const formattedValue = hasLeastOneNestedKey
      ? formatHelpers.getNestedValue(data, field.name)
        || formatHelpers.getNestedValue(data, field.id)
      : value;
    // DONT TOUCH PRESET VALUES
    if (!field.realInitialValue
      || field.realInitialValue === "{{value}}"
      || formatHelpers.checkObject(field.realInitialValue, false)
      || Array.isArray(field.realInitialValue)) {
      field.value = formattedValue;
    }
    // SAVE INITIAL VALUE
    if (formattedValue !== undefined) {
      field.initialValue = formattedValue;
    }
    // Merge value with parentField when has param merged_value
    if (parentField?.merged_value) {
      parentField.value = Object.assign(parentField.value || {}, {
        [field.name || field.id || '']: field.value
      });
      if (parentField.value !== undefined) {
        parentField.initialValue = parentField.value;
      }
    }
  }
  return field;
}

export const pluginProjectsStore = {
  state: {
    savedApiResponse: {
      api_estimate: {
        volume: 0,
        distance: 0,
      },
      api_meeting: {
        duration: 0,
        language: 'Fr',
        operators: [],
        meetingDate: new Date(),
      },
    },
    configLoading: false,
    configError: false,
    nextSheetLoading: false,
    nextSheetError: false,
    closeSheetLoading: false,
    closeSheetError: false,
    project: {},
    projectError: false,
    projectLoading: false,
    allowNext: undefined,
    isSaveLoading: false,
    isActionLoading: false,
    selectedField: null,
    errorField: false,
    isLoadedProject: false,
  },
  actions: {
    ...customFieldsActions,
    getNextSheet(query: string) {
      this.nextSheetLoading = true;
      this.nextSheetError = false;
      return window.connector
        .getNextSheet(query)
        .then(async (result: NextSheet) => {
          useDashboardStore().dashboard.nextProjects = result.count;
          if (window.connector.projectToken !== result.token) {
            this.resetProject();
            window.connector.setProjectId(result.crm_id);
            window.connector.setProjectToken(result.token);
          }
        })
        .catch((error: string) => {
          this.nextSheetError = true;
          throw new Error(error);
        })
        .finally(() => {
          this.nextSheetLoading = false;
        });
    },
    getCloseSheet(query?: string) {
      this.closeSheetLoading = true;
      this.closeSheetError = false;
      return window.connector
        .getCloseSheet(query)
        .then()
        .catch((error: string) => {
          this.closeSheetError = true;
          throw new Error(error);
        })
        .finally(() => {
          this.closeSheetLoading = false;
        });
    },
    getOpenSheet() {
      this.projectLoading = true;
      this.projectError = false;
      return window.connector
        .getOpenSheet()
        .then()
        .catch((error: string) => {
          this.projectError = true;
          throw new Error(error);
        })
        .finally(() => {
          this.projectLoading = false;
        });
    },
    getProject({setInitFilters}) {
      this.projectLoading = true;
      return window.connector
        .getProject()
        .then(async (result: { config: FormConfig; dgt: Project }) => {
          if (result) {
            if (result.dgt) {
              window.connector.setProjectToken(result.dgt.token);
              window.connector.setProjectId(result.dgt.id);
              this.setProject(result.dgt, result.dgt.tasks);
            }
            this.saveMeetingInfos();
            if (result.config) {
              this.setConfig(result.config);
            }
            if (setInitFilters) {
              this.useContactsStore().setInitialFilters();
            }
          }
          this.projectError = false;
        })
        .catch((error: string) => {
          this.projectError = true;
          throw new Error(error);
        })
        .finally(() => {
          this.projectLoading = false;
        });
    },
    setConfig(config: FormConfig) {
      if (config) {
        this.initialConfig = config;
      }
      this.steps = [...this.initialConfig?.steps || []];
      if (this.project) {
        this.setInitialData();
      }
      [
        {
          id: "scrollYAuto",
          name: "scroll_y_auto",
        },
        {
          id: "fixedHeaderHeight",
          name: "fixed_header_height",
        },
      ].forEach((setting: {
        id: string;
        name: string;
      }) => {
        this[setting.id] = this.initialConfig[setting.name];
      });
      this.totalSteps = this.steps.length;
      this.currentStep = this.steps[this.currentStepNb || 0];
      this.currentStep.readyForAutoScroll = true;
      if (this.initialConfig?.header) {
        this.headerConfig = this.initialConfig.header;
      }
    },
    setInitialData() {
      this.allowNext = undefined;
      this.errorField = false;
      if (this.initialData) {
        for (const [index, step] of Object.entries(this.steps) as [string, Step][]) {
          let nextStep = step.next_step;
          let refFieldCondition = true;
          step.initialNextStep = step.next_step;
          for (const field of step.fields as Field[]) {
            if (field.realInitialValue === undefined) {
              field.realInitialValue = field.value;
            }
            // Merge subfields with data 'project' -> initialData (not touched)
            for (const [id, value] of Object.entries(this.initialData)) {
              if (field.fields && formatHelpers.checkObject(field.fields)) {
                for (const subField of Object.values(field.fields) as Field[]) {
                  // Merge fields with data 'project' -> initialData (not touched)
                  setInitialValue(subField, value, id, field);
                }
              }
              if (!field.merged_value) {
                // Merge field with data 'project' -> initialData (not touched)
                setInitialValue(field, value, id);
              }
            }
            const fieldTypeNumber = field.type === "number";
            if (!field.hasOwnProperty("value")) {
              const getValue = formatHelpers.mappingValueFromKey(field.name || field.id, this.initialData, /((.*)+)/g);
              field.value = !!getValue ? getValue : (fieldTypeNumber ? 0 : "");
            }
            if (!field.hasOwnProperty("initialValue")) {
              this.initialData[field.name || field.id] = field.value;
              field.initialValue = field.value;
            }
            const checkHasErrorField = !!(field && fieldUtils.isErrorField(field, undefined, true));
            if (!checkHasErrorField) {
              // Initialize error in left sidebar
              field.hasError = undefined;
              field.errorList = undefined;
              if (!this.errorField) {
                // Force next step when has error field is false
                this.errorField = checkHasErrorField;
                if (!this.errorField) {
                  if (step.force_next_step) {
                    // eslint-disable-next-line radix
                    this.currentStepNb = Number.parseInt(index);
                    field.visibility = true;
                    this.currentStepNb += 1;
                  }
                }
              }
            }
            // Init display menu
            // When field.type === 'next-step'
            if (field.type === 'next-step') {
              refFieldCondition = fieldUtils.isValidRefFields(
                field.ref_fields,
                this.steps,
              );
              nextStep = field.value;
            } else if (field.type === 'allow-next') {
              field.visibility = false;
              if (!this.allowNext) {
                this.allowNext = (field.ref_fields
                  ? fieldUtils.isValidRefFields(field.ref_fields, this.steps)
                  : true
                ) && field.value;
              }
            }
          }
          fieldUtils.displayStepMenu({value: nextStep, oldStep: step.next_step, refFieldCondition}, this.steps);
        }
      }
    },
    updateProject(
      request?: {
        formData: {
          primary_address: Address;
          alt_address: Address;
        };
      },
      isSetDataSaving?: boolean,
    ) {
      isSetDataSaving = isSetDataSaving || typeof isSetDataSaving !== "boolean";
      if (isSetDataSaving) {
        this.setDataSaving(true);
      }
      this.isSaveLoading = true;
      if (request?.formData) {
        request.formData = Object.assign(request.formData, {
          ...formatAddress("primary_address", undefined, request.formData.primary_address),
          ...formatAddress("alt_address", undefined, request.formData.alt_address),
        });
      }
      return window.connector
        .postSaveData(request)
        .then((result: { data: Project }) => {
          if (result?.data) {
            this.setProject(result.data, this.project.tasks);
            this.saveMeetingInfos();
            this.setConfig(result.config || this.initialConfig);
          }
          this.dataError = false;
        })
        .catch((error: { error: string }) => {
          if (error.error === "Could not calculate distance.") {
            this.messageError = i18n.global.t("base.messageError.calculateDistance");
          } else {
            this.messageError = i18n.global.t("base.messageError.saveProject");
          }
          this.dataError = true;
          throw new Error(error.error);
        })
        .finally(() => {
          if (isSetDataSaving) {
            this.setDataSaving(false);
          }
          this.isSaveLoading = false;
        });
    },
    setProject(dgt: Project, tasks: []) {
      if (dgt) {
        dgt = Object.assign(dgt, {
          alt_address: formatAddress("alt_address", dgt),
          primary_address: formatAddress("primary_address", dgt),
        });
        this.initialData = dgt;
        this.project = projectsHelper.formatProject(
          dgt,
          tasks,
          this.useUsersStore().users,
          this.useTasksStore().listTasksType,
          this.useAuthStore().currentUser,
        );
      }
    },
    resetProject() {
      this.steps = [];
      this.initialData = {};
      this.initialConfig = {};
      this.totalSteps = 0;
      this.currentStep = {};
      this.currentStepNb = 0;
      if (this.tab === 'steps') {
        this.tab = 'project';
      }
      this.formData = {};
      this.allowNext = undefined;
      this.errorField = false;
      this.isLoadedProject = false;
      this.savedApiResponse = {
        api_estimate: {
          volume: 0,
          distance: 0,
        },
        api_meeting: {
          duration: 30,
          language: 'FR',
          operators: [],
          meetingDate: new Date(),
          description: '',
          typeMeeting: '',
        },
      };
      useMeetingStore().operators = [];
      useMeetingStore().duration = 0;
      useMeetingStore().description = '';
      useMeetingStore().typeMeeting = '';
      useMeetingStore().meetingDate = '';
      projectsActions.resetProject();
    },
    saveMeetingInfos() {
      useMeetingStore().typeMeeting = this.project.visit_type;
      if (this.project?.meeting) {
        if (this.project.meeting.assignedUser) {
          useMeetingStore().operators = this.project.meeting.assignedUser ? [this.project.meeting.assignedUser.address_state] : [];
        }
        useMeetingStore().description = this.project.meeting.description;
        useMeetingStore().duration = this.project.meeting.duration_minutes + this.project.meeting.duration_hours * 60;
        useMeetingStore().meetingDate = this.project.meeting.date_start;
      }
    },
  },
};
